import i18n from '@/plugins/VueI18n';
import { PMStructureTypeEnum } from '@/ship/Enums/ProjectManagementEnums';

/**
 * Принимает на вход массив выбранных задач структуры для удаления,
 * на основе кол-ва и типа выбранных элементов формирутет заголовок и описание для вывода
 *
 * Использует только два состояния pluarization: первое и вторее (для ед.числа и для 2|3|4|5)
 * @param {Array} selectedTasks
 * @returns {Object}
 */
export function getDeleteTexts(selectedTasks: { type: PMStructureTypeEnum }[]) {
    if (!selectedTasks.length) {
        return {
            confirm: '',
            description: '',
        };
    }

    const countByType: Record<PMStructureTypeEnum, number> = {
        [PMStructureTypeEnum.Task]: 0,
        [PMStructureTypeEnum.Stage]: 0,
        [PMStructureTypeEnum.Mark]: 0,
    };

    for (const task of selectedTasks) {
        countByType[task.type]++;
    }

    const typeMap: Record<PMStructureTypeEnum, { confirmKey: string; descriptionKey: string }> = {
        [PMStructureTypeEnum.Task]: {
            confirmKey: 'page.projectManagement.deleteModal.deleteTask',
            descriptionKey: 'page.projectManagement.deleteModal.task',
        },
        [PMStructureTypeEnum.Stage]: {
            confirmKey: 'page.projectManagement.deleteModal.deleteGroup',
            descriptionKey: 'page.projectManagement.deleteModal.group',
        },
        [PMStructureTypeEnum.Mark]: {
            confirmKey: 'page.projectManagement.deleteModal.deleteMilestone',
            descriptionKey: 'page.projectManagement.deleteModal.milestone',
        },
    };

    const buildParts = (keyType: 'confirmKey' | 'descriptionKey') =>
        Object.keys(countByType)
            .filter((type) => countByType[Number(type) as PMStructureTypeEnum] > 0)
            .map((type) =>
                i18n.tc(
                    typeMap[Number(type) as PMStructureTypeEnum][keyType],
                    normalizeCountForI18n(countByType[Number(type) as PMStructureTypeEnum]),
                ),
            );

    const confirmParts = buildParts('confirmKey');
    const descriptionParts = buildParts('descriptionKey');

    const typesString = joinWithAnd(confirmParts);
    const subjects = joinWithAnd(descriptionParts);

    const isPlural = selectedTasks.length > 1;
    const verb = i18n.t(
        isPlural
            ? 'page.projectManagement.deleteModal.willBePlural'
            : 'page.projectManagement.deleteModal.willBeSingular',
    );
    const deleted = i18n.t(
        isPlural
            ? 'page.projectManagement.deleteModal.deletedPlural'
            : 'page.projectManagement.deleteModal.deletedSingular',
    );

    const hasGroups = countByType[PMStructureTypeEnum.Stage] > 0;
    const extra = hasGroups ? i18n.t('page.projectManagement.deleteModal.includingNested') : '';

    const title = i18n.t('page.projectManagement.deleteModal.confirm', { types: typesString }).toString();
    const description = i18n
        .t('page.projectManagement.deleteModal.descriptionTemplate', {
            subjects,
            verb,
            deleted,
            extra,
        })
        .toString();
    return {
        title: capitalizeFirstLetter(title),
        description: capitalizeFirstLetter(description),
    };
}

function normalizeCountForI18n(count: number): number {
    return count === 1 ? 1 : 2;
}

function joinWithAnd(items: string[]): string {
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} и ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} и ${items[items.length - 1]}`;
}

function capitalizeFirstLetter(val: string) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}
